import OnvoBase, { JobUpdateEvent } from "../base.js";
import { Widget } from "../types/index.js";

interface WidgetCacheResponse {
  data: any;
  pagination: {
    page: number;
    pageSize: number;
    totalPages: number;
    totalRows: number;
  };
}

export class OnvoWidget extends OnvoBase {
  #id: string;

  constructor(id: string, apiKey: string, options?: { endpoint: string }) {
    super(apiKey, options);
    this.#id = id;
  }

  /**
   * Exports the widget in the specified format.
   *
   * @param {("svg" | "csv" | "xlsx" | "png" | "jpeg")} format - The format to export the widget in.
   * @return {Promise<Blob>} A promise that resolves to a Blob representing the exported widget.
   */
  export(format: "svg" | "csv" | "xlsx" | "png" | "jpeg", theme: "light" | "dark" = "light", callback?: (event: JobUpdateEvent) => void): Promise<Blob> {
    return this._fetchStreamBase(
      `/api/widgets/${this.#id}/export?theme=${theme}&format=${format}`,
      "GET",
      undefined,
      callback
    ) as Promise<Blob>;
  }
  /**
   * Returns the cached widget data.
   * 
   * @param {Object} [props] - Configuration options
   * @param {boolean} [props.raw] - Whether to return raw data
   * @param {number} [props.page] - Page number for pagination
   * @param {number} [props.pageSize] - Number of items per page
   * @param {string} [props.sortField] - Field to sort by
   * @param {'asc'|'desc'} [props.sortDirection] - Sort direction
   * @return {Promise<WidgetCacheResponse>} A promise that resolves to the cached widget data with pagination information.
   */
  cache(props?: {
    raw?: boolean;
    page?: number;
    pageSize?: number;
    sortField?: string;
    sortDirection?: 'asc' | 'desc';
  }): Promise<WidgetCacheResponse> {
    const params = new URLSearchParams();
    if (props?.raw) params.append('raw', 'true');
    if (props?.page) params.append('page', props.page.toString());
    if (props?.pageSize) params.append('pageSize', props.pageSize.toString());
    if (props?.sortField) params.append('sortField', props.sortField);
    if (props?.sortDirection) params.append('sortDirection', props.sortDirection);

    const queryString = params.toString();
    return this._fetchBase(
      `/api/widgets/${this.#id}/cache${queryString ? `?${queryString}` : ""}`
    ) as Promise<WidgetCacheResponse>;
  }
  /**
   * Updates the prompts for the widget.
   *
   * @param {Array<{ role: "user" | "assistant"; content: String }>} messages - The new prompts for the widget.
   * @return {Promise<Widget>} A promise that resolves to the updated widget.
   */
  updatePrompts(
    messages: { role: "user" | "assistant"; content: String }[],
    callback?: (event: JobUpdateEvent) => void
  ): Promise<Widget> {
    return this._fetchStreamBase("/api/widgets/" + this.#id, "PATCH", {
      messages,
    },
      callback
    ) as Promise<Widget>;
  }
  /**
   * Executes the given code in the widget.
   *
   * @return {Promise<Widget & { _meta: { status: string, url: string, total?: number, completed?: number } }>} A promise that resolves to the result of executing the code.
   */
  updateCache(callback?: (event: JobUpdateEvent) => void): Promise<Widget & { _meta: { status: string, url: string, total?: number, completed?: number } }> {
    return this._fetchStreamBase(
      "/api/widgets/" + this.#id + "/cache",
      "POST",
      undefined,
      callback
    ) as Promise<Widget & { _meta: { status: string, url: string, total?: number, completed?: number } }>;
  }
}
